/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';

import { getClientDefaultDeliveryAddressAsync } from './DataService/DataActionExtension.g';
import { IGetClientDefaultDeliveryAddressResponse } from './DataService/DataServiceEntities.g';

/**
 * GetClientDefaultDeliveryAddress Input Action
 */
export class GetClientDefaultDeliveryAddressInput implements IActionInput {
    public accountNum: string;

    constructor(accountNum: string) {
        this.accountNum = accountNum;
    }

    public getCacheKey = () => `GetClientDefaultDeliveryAddress-${this.accountNum}`;
    public getCacheObjectType = () => 'GetClientDefaultDeliveryAddressResponse';
    public dataCacheType = (): CacheType => 'none';
}

const createInput = (args: ICreateActionContext): IActionInput => {
    const accountNum = (args.requestContext as any)?.user?.customerAccountNumber || '';
    return new GetClientDefaultDeliveryAddressInput(accountNum);
};

export async function GetClientDefaultDeliveryAddress(
    input: GetClientDefaultDeliveryAddressInput,
    ctx: IActionContext
): Promise<IGetClientDefaultDeliveryAddressResponse> {
    if (!input.accountNum) {
        return { Success: false, ErrorMessage: 'Account number is required', LocationId: undefined };
    }

    return getClientDefaultDeliveryAddressAsync({ callerContext: ctx }, input.accountNum)
        .then(data => {
            return data;
        })
        .catch(error => {
            ctx.telemetry.exception(error);
            ctx.trace('[get-client-default-delivery-address] Unable to load client default delivery address');
            throw new Error('[get-client-default-delivery-address] Unable to load client default delivery address');
        });
}

export default createObservableDataAction({
    action: <IAction<IGetClientDefaultDeliveryAddressResponse>>GetClientDefaultDeliveryAddress,
    id: 'GetClientDefaultDeliveryAddress',
    input: createInput
});
